// Checkout page + TopupReturnPage strings — namespace `checkout`
export default {
  en: {
    secureCheckout: 'Secure checkout',
    backToCart: 'Back to cart',
    title: 'Checkout',

    steps: {
      label: 'Checkout progress',
      cart: 'Cart',
      details: 'Details',
      payment: 'Payment',
      done: 'Done',
    },

    emailVerification: {
      message: 'Please verify your email to continue.',
      cta: 'Go to settings',
    },

    empty: {
      heading: 'Your cart is empty',
      body: 'Add a few keys before checking out.',
      cta: 'Browse catalog',
    },

    billing: {
      heading: 'Billing details',
      accountLabel: 'Account',
      addressLabel: 'Address',
      countryLabel: 'Country',
      editLink: 'Edit in Settings',
      missingBody: 'Add your billing address to continue.',
      missingNote: 'EU compliance requires a billing address on file before placing an order.',
      missingCta: 'Add in Settings',
    },

    payment: {
      heading: 'Payment method',
      walletNote: 'Orders are paid from your GAMZOR wallet. Top up with card or PayPal first.',
      walletTitle: 'GAMZOR wallet',
      available: '{amount} available',
      sufficient: 'Sufficient funds for this transaction.',
      insufficient: 'Not enough funds — top up to complete this order.',
      topUpHeading: 'Add funds to your wallet',
      cardTitle: 'Card via Stripe',
      cardBody: 'Top up with Visa or Mastercard.',
      paypalTitle: 'PayPal',
      paypalBody: 'Top up from your PayPal account.',
    },

    errors: {
      someItemsFailed: "Some items couldn't be ordered:",
      successfulMoved: 'Successful items moved to your orders; the rest remain in your cart.',
      fallback: 'Could not place all orders',
      stepFallback: 'Failed',
    },

    summary: {
      heading: 'Order summary',
      itemCount: '{count} item in cart | {count} items in cart',
      subtotal: 'Subtotal',
      total: 'Total',
      vatNote: 'A VAT invoice is issued with your order.',
      show: 'Show order summary',
      hide: 'Hide order summary',
      trust: {
        delivery: 'Instant key delivery',
        seller: 'EU seller · VAT invoice',
        payments: 'Stripe & PayPal',
      },
    },

    placeOrder: 'Place Order — {total}',
    placingOrder: 'Placing order…',

    terms: {
      before: 'I agree to the ',
      tos: 'Terms of Service',
      after: " and confirm I'm purchasing a digital product with instant delivery. Unrevealed keys are refundable.",
    },

    legalNote:
      'GAMZOR is a marketplace: keys are sold by independent EU sellers and delivered digitally. Payments are processed by Stripe and PayPal; card data never touches our servers.',

    footer: {
      privacy: 'Privacy',
      terms: 'Terms',
      cookies: 'Cookies',
      contact: 'Contact',
      copyright: '© {year} gamzor.net. All rights reserved.',
    },

    topup: {
      missingRef: 'Missing payment reference',
      loadError: 'Could not load top-up status',
      errorTitle: 'Could not check this top-up',
      stillPending: {
        heading: 'Still pending',
        body: "The provider hasn't confirmed yet. You won't be charged twice — refresh this page later or open your wallet to see the credit when it lands.",
      },
      waiting: 'Waiting {sec}s·we’ll update automatically',
      redirecting: 'Redirecting to wallet…',
      goToWallet: 'Go to wallet',
      backToShop: 'Back to shop',
      loading: 'Checking your payment…',
      status: {
        PENDING: {
          title: 'Waiting for confirmation…',
          sub: 'GAMZOR will confirm the payment in a moment. This page updates automatically.',
        },
        COMPLETED: {
          title: 'Top-up successful',
          sub: 'Your wallet has been credited.',
        },
        FAILED: {
          title: 'Top-up failed',
          sub: 'The payment was not completed. Your wallet was not charged.',
        },
        REFUNDED: {
          title: 'Top-up refunded',
          sub: 'The payment was reversed by the provider.',
        },
      },
    },
  },

  cs: {
    secureCheckout: 'Zabezpečená pokladna',
    backToCart: 'Zpět do košíku',
    title: 'Pokladna',

    steps: {
      label: 'Průběh objednávky',
      cart: 'Košík',
      details: 'Údaje',
      payment: 'Platba',
      done: 'Hotovo',
    },

    emailVerification: {
      message: 'Prosím ověřte svůj e-mail, abyste mohli pokračovat.',
      cta: 'Přejít do nastavení',
    },

    empty: {
      heading: 'Váš košík je prázdný',
      body: 'Přidejte klíče před přechodem k pokladně.',
      cta: 'Procházet katalog',
    },

    billing: {
      heading: 'Fakturační údaje',
      accountLabel: 'Účet',
      addressLabel: 'Adresa',
      countryLabel: 'Země',
      editLink: 'Upravit v nastavení',
      missingBody: 'Přidejte fakturační adresu pro pokračování.',
      missingNote: 'Regulace EU vyžaduje fakturační adresu před zadáním objednávky.',
      missingCta: 'Přidat v nastavení',
    },

    payment: {
      heading: 'Způsob platby',
      walletNote: 'Objednávky se platí z vaší peněženky GAMZOR. Nejprve ji dobijte kartou nebo PayPalem.',
      walletTitle: 'Peněženka GAMZOR',
      available: '{amount} k dispozici',
      sufficient: 'Dostatek prostředků pro tuto transakci.',
      insufficient: 'Nedostatek prostředků — dobijte peněženku pro dokončení objednávky.',
      topUpHeading: 'Přidat prostředky do peněženky',
      cardTitle: 'Karta přes Stripe',
      cardBody: 'Dobijte kartou Visa nebo Mastercard.',
      paypalTitle: 'PayPal',
      paypalBody: 'Dobijte z účtu PayPal.',
    },

    errors: {
      someItemsFailed: 'Některé položky nebylo možné objednat:',
      successfulMoved: 'Úspěšné položky byly přesunuty do vašich objednávek; zbytek zůstává v košíku.',
      fallback: 'Nepodařilo se zadat všechny objednávky',
      stepFallback: 'Selhalo',
    },

    summary: {
      heading: 'Přehled objednávky',
      itemCount: '{count} položka v košíku | {count} položky v košíku | {count} položek v košíku',
      subtotal: 'Mezisoučet',
      total: 'Celkem',
      vatNote: 'K objednávce vystavíme fakturu s DPH.',
      show: 'Zobrazit přehled objednávky',
      hide: 'Skrýt přehled objednávky',
      trust: {
        delivery: 'Okamžité doručení klíče',
        seller: 'Prodejce z EU · faktura s DPH',
        payments: 'Stripe a PayPal',
      },
    },

    placeOrder: 'Zadat objednávku — {total}',
    placingOrder: 'Zadávání objednávky…',

    terms: {
      before: 'Souhlasím s ',
      tos: 'Podmínkami služby',
      after: ' a potvrzuji, že kupuji digitální produkt s okamžitým doručením. Neodhalené klíče jsou vratné.',
    },

    legalNote:
      'GAMZOR je tržiště: klíče prodávají nezávislí prodejci z EU a doručují se digitálně. Platby zpracovávají Stripe a PayPal; údaje karty k nám nikdy nedorazí.',

    footer: {
      privacy: 'Ochrana soukromí',
      terms: 'Podmínky',
      cookies: 'Cookies',
      contact: 'Kontakt',
      copyright: '© {year} gamzor.net. Všechna práva vyhrazena.',
    },

    topup: {
      missingRef: 'Chybí reference platby',
      loadError: 'Nepodařilo se načíst stav dobíjení',
      errorTitle: 'Dobití se nepodařilo ověřit',
      stillPending: {
        heading: 'Stále čeká na potvrzení',
        body: 'Poskytovatel zatím nepotvrdil platbu. Nebudete účtováni dvakrát — načtěte tuto stránku znovu nebo otevřete peněženku, abyste viděli kredit, jakmile dorazí.',
      },
      waiting: 'Čekáme {sec} s·aktualizujeme automaticky',
      redirecting: 'Přesměrování do peněženky…',
      goToWallet: 'Přejít do peněženky',
      backToShop: 'Zpět do obchodu',
      loading: 'Ověřujeme vaši platbu…',
      status: {
        PENDING: {
          title: 'Čekání na potvrzení…',
          sub: 'GAMZOR platbu za okamžik potvrdí. Tato stránka se aktualizuje automaticky.',
        },
        COMPLETED: {
          title: 'Dobíjení proběhlo úspěšně',
          sub: 'Vaše peněženka byla připsána.',
        },
        FAILED: {
          title: 'Dobíjení selhalo',
          sub: 'Platba nebyla dokončena. Vaše peněženka nebyla zatížena.',
        },
        REFUNDED: {
          title: 'Dobíjení bylo vráceno',
          sub: 'Platbu stornoval poskytovatel.',
        },
      },
    },
  },
};
